import { MONTHS, type CustomerRevenueReportItem, type RevenuePerMonth, type YearlyRevenue } from '@vy/types';
import { entries, extract, isNullOrUndefined, purge, uniqueBy } from '@vy/utils';

export function getTotalCustomerRevenueDateHeaders(reportItems: CustomerRevenueReportItem[]): string[] {
  const yearlyRevenues = extract(reportItems, 'yearlyRevenue');
  const dateStrings = yearlyRevenues.flatMap(getYearlyRevenueDateStrings);
  const uniqueDateStrings = uniqueBy(dateStrings, (dateString) => dateString);

  const monthIndices = uniqueDateStrings.map(toAbsoluteMonthIndex);
  if (monthIndices.length === 0) {
    return [];
  }

  const firstMonth = Math.min(...monthIndices);
  const lastMonth = Math.max(...monthIndices);

  const headers: string[] = [];
  for (let monthIndex = firstMonth; monthIndex <= lastMonth; monthIndex++) {
    headers.push(toDateHeader(monthIndex));
  }

  return headers;
}

function toAbsoluteMonthIndex(dateString: string): number {
  const separatorIndex = dateString.lastIndexOf(' ');
  const month = dateString.slice(0, separatorIndex);
  const year = Number(dateString.slice(separatorIndex + 1));

  return year * MONTHS.length + MONTHS.indexOf(month as (typeof MONTHS)[number]);
}

function toDateHeader(absoluteMonthIndex: number): string {
  const year = Math.floor(absoluteMonthIndex / MONTHS.length);
  const month = MONTHS[absoluteMonthIndex % MONTHS.length];

  return `${month} ${year}`;
}

function getYearlyRevenueDateStrings(yearlyRevenue: YearlyRevenue) {
  return entries(yearlyRevenue).reduce<string[]>((dateStrings, [year, revenuePerMonth]) => {
    const revenuePerMonthStrings = getRevenuePerMonthStrings(revenuePerMonth, year);
    const purgedRevenuePerMonthStrings = purge(revenuePerMonthStrings);

    return [...dateStrings, ...purgedRevenuePerMonthStrings];
  }, []);
}

function getRevenuePerMonthStrings(revenuePerMonth: RevenuePerMonth, year: string | number) {
  return entries(revenuePerMonth).map(([monthKey, revenuePerMonthBreakdown]) => {
    if (isNullOrUndefined(revenuePerMonthBreakdown)) {
      return undefined;
    }

    return `${monthKey} ${year}`;
  });
}
